import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  type Firestore,
  type FirestoreDataConverter,
  type Unsubscribe,
} from 'firebase/firestore';
import {
  validateBlockLibraryExampleWrite,
  type BlockLibraryExampleDoc,
  type BlockLibraryExampleInput,
  type CliBlockLibraryMutationData,
  type CliBlockLibraryMutationOperation,
  type CliBlockLibraryMutationReadback,
} from '@modules/shared-types';
import { blockLibraryConverter } from '../converters/blockLibraryConverter';
import { getBlockLibraryCliApiBaseUrl } from './BlockLibraryMutationApi';
import { CoreFirebaseService } from './CoreFirebaseService';

export class BlockLibraryServiceError extends Error {
  constructor(
    readonly code:
      | 'invalid_input'
      | 'limit_exceeded'
      | 'not_found'
      | 'conflict'
      | 'auth_required'
      | 'server_error',
    message: string,
    readonly errors: string[] = [],
  ) {
    super(message);
    this.name = 'BlockLibraryServiceError';
  }
}

/**
 * Client reader for projects/{projectId}/blockLibrary.
 *
 * Reads and subscriptions intentionally stay on the Firebase client SDK. All
 * mutations use the authenticated Agent/CLI surface, its server-owned v2 validator, and its
 * dry-run -> receipt-bound apply -> independent readback protocol. Firestore
 * rules deny direct browser writes to both the records and the catalogue lock.
 */
export class BlockLibraryService {
  private readonly firestore: Firestore;

  constructor(
    private readonly projectId: string,
    private readonly converter: FirestoreDataConverter<BlockLibraryExampleDoc> = blockLibraryConverter,
    firestore?: Firestore,
  ) {
    this.firestore = firestore ?? CoreFirebaseService.getInstance().firestore;
  }

  private get collectionPath(): string {
    return `projects/${this.projectId}/blockLibrary`;
  }

  async list(options: { enabledOnly?: boolean } = {}): Promise<BlockLibraryExampleDoc[]> {
    const snapshot = await getDocs(
      collection(this.firestore, this.collectionPath).withConverter(this.converter),
    );
    return snapshot.docs
      .map((item) => item.data())
      .filter((item) => !options.enabledOnly || item.enabled)
      .sort(sortExamples);
  }

  async get(id: string): Promise<BlockLibraryExampleDoc | null> {
    const snapshot = await getDoc(
      doc(this.firestore, this.collectionPath, id).withConverter(this.converter),
    );
    return snapshot.exists() ? snapshot.data() : null;
  }

  async create(
    input: BlockLibraryExampleInput,
    // Retained for public API compatibility. Audit identity is derived from the
    // authenticated Firebase bearer token by Builder and cannot be spoofed.
    _actorId?: string | null,
  ): Promise<BlockLibraryExampleDoc> {
    const validated = requireValidInput(input);
    // Firestore generates this id locally; no write happens. Holding the id
    // before dry-run makes the exact target stable across both HTTP phases.
    const exampleId = doc(collection(this.firestore, this.collectionPath)).id;
    requireValidExampleId(exampleId);

    const dryRun = await this.mutate('POST', exampleId, 'create', {
      example: validated,
    });
    const checksum = requireDryRun(dryRun, 'create', this.projectId, exampleId, false);
    const applied = await this.mutate('POST', exampleId, 'create', {
      example: validated,
      apply: true,
      dryRunChecksum: checksum,
    });
    requireApplyReadback(applied, 'create', this.projectId, exampleId, checksum, true);
    return normalizeAppliedExample(applied.example, exampleId);
  }

  async update(
    id: string,
    patch: Partial<BlockLibraryExampleInput>,
    // Retained for public API compatibility; see create().
    _actorId?: string | null,
  ): Promise<BlockLibraryExampleDoc> {
    const exampleId = requireValidExampleId(id);
    // Preserve the former client validation behavior and, importantly, reject
    // invalid/oversized merged records before making any HTTP mutation call.
    const existing = await this.get(exampleId);
    if (!existing) {
      throw new BlockLibraryServiceError('not_found', `Block library example ${exampleId} was not found.`);
    }
    const candidate = requireValidInput({ ...inputFromDoc(existing), ...patch });
    const normalizedPatch = patchFromCandidate(patch, candidate);

    const dryRun = await this.mutate('PUT', exampleId, 'update', {
      patch: normalizedPatch,
    });
    const checksum = requireDryRun(dryRun, 'update', this.projectId, exampleId, true);
    const applied = await this.mutate('PUT', exampleId, 'update', {
      patch: normalizedPatch,
      apply: true,
      dryRunChecksum: checksum,
    });
    requireApplyReadback(applied, 'update', this.projectId, exampleId, checksum, true);
    return normalizeAppliedExample(applied.example, exampleId);
  }

  async delete(id: string): Promise<void> {
    const exampleId = requireValidExampleId(id);
    const dryRun = await this.mutate('POST', exampleId, 'delete', {});
    const checksum = requireDryRun(dryRun, 'delete', this.projectId, exampleId, true);
    const applied = await this.mutate('POST', exampleId, 'delete', {
      apply: true,
      dryRunChecksum: checksum,
    });
    // The apply response is already Builder's independent post-transaction
    // readback. exists:false is terminal; do not add an eventually-consistent
    // Firestore get that could turn a successful delete into a false failure.
    requireApplyReadback(applied, 'delete', this.projectId, exampleId, checksum, false);
    if (applied.deleted !== true) {
      throw invalidServerResponse('Delete apply response did not confirm deleted=true.');
    }
  }

  subscribe(
    onData: (examples: BlockLibraryExampleDoc[]) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    const ref = collection(this.firestore, this.collectionPath).withConverter(this.converter);
    return onSnapshot(ref, (snapshot) => {
      onData(snapshot.docs.map((item) => item.data()).sort(sortExamples));
    }, (error) => onError?.(error));
  }

  private async mutate(
    method: 'POST' | 'PUT',
    exampleId: string,
    operation: 'create' | 'update' | 'delete',
    body: Record<string, unknown>,
  ): Promise<CliBlockLibraryMutationData> {
    const token = await CoreFirebaseService.getInstance().auth.currentUser?.getIdToken();
    if (!token) {
      throw new BlockLibraryServiceError(
        'auth_required',
        'Sign in before changing the project block library.',
      );
    }

    const suffix = operation === 'delete' ? '/delete' : '';
    const response = await fetch(
      `${getBlockLibraryCliApiBaseUrl()}/api/cli/v1/projects/${encodeURIComponent(this.projectId)}`
        + `/block-library/${encodeURIComponent(exampleId)}${suffix}`,
      {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      },
    );
    const payload = await readJsonResponse(response);
    if (!response.ok) throw serverProblem(response.status, payload);
    const data = isRecord(payload) && isRecord(payload.data) ? payload.data : payload;
    if (!isRecord(data)) {
      throw invalidServerResponse('Builder returned no block-library mutation data.');
    }
    return data as unknown as CliBlockLibraryMutationData;
  }
}

function requireValidInput(input: unknown): BlockLibraryExampleInput {
  const validated = validateBlockLibraryExampleWrite(input);
  if (!validated.ok) {
    throw new BlockLibraryServiceError(
      'invalid_input',
      `Block library example is invalid: ${validated.errors.join(' ')}`,
      validated.errors,
    );
  }
  return validated.value;
}

function requireValidExampleId(id: string): string {
  if (!/^[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/.test(id)) {
    throw new BlockLibraryServiceError(
      'invalid_input',
      'Block library example id must be path-safe and at most 128 characters.',
      ['exampleId'],
    );
  }
  return id;
}

function inputFromDoc(example: BlockLibraryExampleDoc): BlockLibraryExampleInput {
  const {
    id: _id,
    createdBy: _createdBy,
    createdAt: _createdAt,
    updatedBy: _updatedBy,
    updatedAt: _updatedAt,
    ...input
  } = example;
  return input;
}

/**
 * Preserve patch semantics while forwarding the validator's normalization for
 * every explicitly supplied field (trimmed name/tags, canonical optionals).
 */
function patchFromCandidate(
  patch: Partial<BlockLibraryExampleInput>,
  candidate: BlockLibraryExampleInput,
): Partial<BlockLibraryExampleInput> {
  const normalized: Partial<BlockLibraryExampleInput> = {};
  for (const key of Object.keys(patch) as Array<keyof BlockLibraryExampleInput>) {
    if (Object.prototype.hasOwnProperty.call(candidate, key)) {
      (normalized as Record<string, unknown>)[key] = candidate[key];
    } else {
      // JSON has no undefined. Null deliberately clears an optional server-side
      // field and is accepted by the shared validator's merge path.
      (normalized as Record<string, unknown>)[key] = null;
    }
  }
  return normalized;
}

function requireDryRun(
  data: CliBlockLibraryMutationData,
  operation: CliBlockLibraryMutationOperation,
  projectId: string,
  exampleId: string,
  expectedExists: boolean,
): string {
  requireMutationIdentity(data, operation, projectId, exampleId);
  if (data.dryRun !== true || data.applied !== false) {
    throw invalidServerResponse('Builder dry-run flags are invalid.');
  }
  requireReadback(data.readback, expectedExists, 'dry-run');
  const checksum = data.receipt?.dryRunChecksum;
  if (typeof checksum !== 'string' || !/^sha256:[a-f0-9]{64}$/.test(checksum)) {
    throw invalidServerResponse('Builder dry-run receipt has no valid checksum.');
  }
  return checksum;
}

function requireApplyReadback(
  data: CliBlockLibraryMutationData,
  operation: CliBlockLibraryMutationOperation,
  projectId: string,
  exampleId: string,
  dryRunChecksum: string,
  expectedExists: boolean,
): void {
  requireMutationIdentity(data, operation, projectId, exampleId);
  if (data.dryRun !== false || data.applied !== true) {
    throw invalidServerResponse('Builder apply flags are invalid.');
  }
  if (data.receipt.dryRunChecksum !== dryRunChecksum) {
    throw invalidServerResponse('Builder apply receipt does not match the reviewed dry-run.');
  }
  requireReadback(data.readback, expectedExists, 'apply');
}

function requireMutationIdentity(
  data: CliBlockLibraryMutationData,
  operation: CliBlockLibraryMutationOperation,
  projectId: string,
  exampleId: string,
): void {
  const receipt = data.receipt;
  if (!isRecord(receipt)
    || receipt.schemaVersion !== 'block-library-mutation-receipt/v1'
    || receipt.operation !== operation
    || receipt.scope !== 'project'
    || receipt.projectId !== projectId
    || receipt.exampleId !== exampleId
    || !Number.isSafeInteger(receipt.catalogRevision)
    || (receipt.catalogRevision as number) < 0) {
    throw invalidServerResponse('Builder mutation receipt target or revision is invalid.');
  }
}

function requireReadback(
  readback: CliBlockLibraryMutationReadback,
  expectedExists: boolean,
  phase: string,
): void {
  if (!isRecord(readback) || readback.exists !== expectedExists) {
    throw invalidServerResponse(`Builder ${phase} readback has an unexpected target state.`);
  }
  const checksum = (readback as { checksum?: unknown }).checksum;
  if (expectedExists
    && (typeof checksum !== 'string' || !/^sha256:[a-f0-9]{64}$/.test(checksum))) {
    throw invalidServerResponse(`Builder ${phase} readback has no valid checksum.`);
  }
}

function normalizeAppliedExample(value: unknown, expectedId: string): BlockLibraryExampleDoc {
  if (!isRecord(value) || value.id !== expectedId || typeof value.createdBy !== 'string') {
    throw invalidServerResponse('Builder apply response has no authoritative example readback.');
  }
  const createdAt = parseServerDate(value.createdAt, 'createdAt');
  const updatedAt = value.updatedAt === undefined
    ? undefined
    : parseServerDate(value.updatedAt, 'updatedAt');
  const validated = requireServerValidInput(value);
  return {
    id: expectedId,
    ...validated,
    createdBy: value.createdBy,
    createdAt,
    ...(typeof value.updatedBy === 'string' ? { updatedBy: value.updatedBy } : {}),
    ...(updatedAt ? { updatedAt } : {}),
  };
}

function requireServerValidInput(value: Record<string, unknown>): BlockLibraryExampleInput {
  const validated = validateBlockLibraryExampleWrite(value);
  if (!validated.ok) {
    throw invalidServerResponse(
      `Builder apply example failed the shared v2 validator: ${validated.errors.join(' ')}`,
    );
  }
  return validated.value;
}

function parseServerDate(value: unknown, field: string): Date {
  if (typeof value !== 'string') {
    throw invalidServerResponse(`Builder apply example ${field} is not an ISO date.`);
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    throw invalidServerResponse(`Builder apply example ${field} is not an ISO date.`);
  }
  return parsed;
}

async function readJsonResponse(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    if (!response.ok) {
      throw new BlockLibraryServiceError(
        'server_error',
        `Block library request failed with HTTP ${response.status}.`,
      );
    }
    throw invalidServerResponse('Builder returned a non-JSON mutation response.');
  }
}

function serverProblem(status: number, payload: unknown): BlockLibraryServiceError {
  const problem = isRecord(payload) ? payload : {};
  const detail = typeof problem.detail === 'string'
    ? problem.detail
    : `Block library request failed with HTTP ${status}.`;
  const errors = Array.isArray(problem.errors)
    ? problem.errors
      .map((entry) => isRecord(entry) && typeof entry.message === 'string' ? entry.message : '')
      .filter(Boolean)
    : [];
  let code: BlockLibraryServiceError['code'] = 'server_error';
  if (status === 400) code = 'invalid_input';
  else if (status === 404) code = 'not_found';
  else if (status === 409) {
    code = detail.includes('at most') ? 'limit_exceeded' : 'conflict';
  } else if (status === 401) code = 'auth_required';
  return new BlockLibraryServiceError(code, detail, errors);
}

function invalidServerResponse(message: string): BlockLibraryServiceError {
  return new BlockLibraryServiceError('server_error', message);
}

function isRecord(value: unknown): value is Record<string, any> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function sortExamples(left: BlockLibraryExampleDoc, right: BlockLibraryExampleDoc): number {
  return left.name.localeCompare(right.name) || left.id.localeCompare(right.id);
}
